import React, { useState } from 'react';
import { Typography, Avatar, useTheme, Stack, Chip, Button } from '@mui/material';
import { PurchaseOrder, PurchaseOrderItem } from '@domain/purchasing';
import { useTimeline } from '@features/purchasing/hooks/useTimeline';
import { isValid } from 'date-fns';
import TimelineItemSegment from './TimelineItemSegment';

interface TimelineItemProps {
    item: PurchaseOrderItem
    groupIndex: number;
    rowIndex: number;
    headerColumnWidth: number;
    weekWidth: number;
    onOrderClick: (order: PurchaseOrder) => void;
    onAddShipmentClick: (item: PurchaseOrderItem) => void;
}

const TimelineItem: React.FC<TimelineItemProps> = ({
    item,
    groupIndex,
    rowIndex,
    headerColumnWidth,
    weekWidth,
    onOrderClick,
    onAddShipmentClick,
}) => {
    const theme = useTheme();
    const index = groupIndex * 100 + rowIndex; // Create unique index for alternating colors
    const [onHover, setOnHover] = useState(false);

    const { getItemStartPosition, getItemEndPosition, getPositionsForDate } = useTimeline({
        weekWidth,
        headerColumnWidth
    });

    return (
        <div key={`${groupIndex}-${rowIndex}`}
            onMouseEnter={() => setOnHover(true)}
            onMouseLeave={() => setOnHover(false)}
            style={{
                backgroundColor: (index) % 2 === 0 ? theme.palette.background.paper : '#f5f5f5',
                position: 'relative',
                display: 'flex',
                flexDirection: 'row',
                boxSizing: 'border-box',
            }}
        >
            {/* Header column */}
            <div
                onClick={() => onOrderClick(item.purchaseOrder!)}
                style={{
                    cursor: 'pointer',
                    backgroundColor: (index) % 2 === 0 ? theme.palette.background.paper : '#f5f5f5',
                    width: headerColumnWidth,
                    position: 'sticky',
                    left: 0,
                    top: 0,
                    bottom: 0,
                    zIndex: 3,
                    height: '100%',
                    borderRight: '1px solid #e0e0e0',
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    padding: '0 20px',
                    boxSizing: 'border-box',

                }}
            >
                <div style={{
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    justifyContent: 'flex-start',
                    gap: 10,
                    padding: '20px 5px',
                    opacity: item.purchaseOrder?.draft ? 0.5 : 1,
                }}>
                    <Stack direction="row" alignItems="center" gap={2}>
                        <Avatar
                            src={item.product.image}
                            variant="rounded"
                            sx={{
                                border: `1px solid ${theme.palette.divider}`,
                                width: 30,
                                height: 30,
                            }}
                        />

                        <Typography variant="body2" color="text.secondary"
                            sx={{
                                overflow: 'hidden',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                display: '-webkit-box',
                                textOverflow: 'ellipsis',
                            }}
                        >
                            {item.product.title}
                        </Typography>
                    </Stack>

                    <Stack direction="row" alignItems="center" gap={2}>
                        <Typography variant="body2" color="text.primary">
                            {item.quantity || 0} items
                        </Typography>

                        {item.purchaseOrder?.draft && (
                            <Chip
                                size="small"
                                color="info"
                                label="Draft"
                            />
                        )}
                    </Stack>
                </div>
            </div>

            {/* Main timeline bar */}
            <div
                style={{
                    position: 'absolute',
                    zIndex: 2,
                    top: 0,
                    left: `${getItemStartPosition(item)}px`,
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxSizing: 'border-box',
                    padding: '10px 0',
                    width: `${getItemEndPosition(item) - getItemStartPosition(item)}px`,
                    opacity: item.purchaseOrder?.draft ? 0.75 : 1,
                }}
            >


                {/* Main timeline bar divided into 3 parts */}
                <Stack
                    onClick={() => onOrderClick(item.purchaseOrder!)}
                    style={{
                        cursor: 'pointer',
                        width: '100%',
                        height: '40px',
                        borderRadius: 10,
                        position: 'relative',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: theme.palette.primary.main + '25',
                    }}>


                    {/* Production segment */}
                    {item.productionStartDate && item.productionReadyDate &&
                        isValid(new Date(item.productionStartDate)) &&
                        isValid(new Date(item.productionReadyDate)) && (
                            <TimelineItemSegment
                                startDate={new Date(item.productionStartDate)}
                                endDate={new Date(item.productionReadyDate)}
                                startOffset={getItemStartPosition(item)}
                                color={theme.palette.primary.main}
                            />
                        )}

                    {/* Shipment segment */}
                    {item.shipment ? (
                        <>
                            {item.shipment.departureDate && item.shipment.arrivalDate && (
                                isValid(new Date(item.shipment.departureDate)) &&
                                isValid(new Date(item.shipment.arrivalDate)) && (
                                    <TimelineItemSegment
                                        startDate={new Date(item.shipment.departureDate)}
                                        endDate={new Date(item.shipment.arrivalDate)}
                                        startOffset={getItemStartPosition(item)}
                                        color={theme.palette.secondary.main}
                                    />
                                )
                            )}

                            {item.shipment?.arrivalDate && item.shipment?.processedDate && (
                                isValid(new Date(item.shipment.arrivalDate)) &&
                                isValid(new Date(item.shipment.processedDate)) && (
                                    <TimelineItemSegment
                                        startDate={new Date(item.shipment.arrivalDate)}
                                        endDate={new Date(item.shipment.processedDate)}
                                        startOffset={getItemStartPosition(item)}
                                        color={theme.palette.success.main}
                                    />
                                )
                            )}
                        </>
                    ) :

                        onHover && (
                            <div style={{
                                position: 'absolute',
                                bottom: 0,
                                left:  getPositionsForDate(item.productionReadyDate || item.purchaseOrder?.orderDate || new Date()) - getItemStartPosition(item),
                                right: 0,
                                height: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                padding: '10px',
                                boxSizing: 'border-box',
                                minWidth: '200px',
                            }}>
                                <Button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onAddShipmentClick(item);
                                    }}
                                    size="small"
                                    variant="contained"
                                >

                                    Add shipment
                                </Button>
                            </div>
                        )}

                </Stack>

            </div>

        </div>
    );
};

export default TimelineItem;
